import { useEffect, useRef, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { CheckCircle2, Clock, XCircle, Loader2 } from "lucide-react";
import { Navbar } from "@/components/Navbar";
import { BrandMark } from "@/components/BrandMark";
import { startCheckout, gbp } from "@/components/PurchaseCard";
import { useAuth } from "@/context/AuthContext";
import { api, apiError } from "@/lib/api";

function Shell({ children, testid }) {
  return (
    <div className="min-h-screen legacy-bg">
      <Navbar />
      <main className="max-w-lg mx-auto px-4 sm:px-6 py-12 sm:py-16">
        <div className="glass-card p-7 sm:p-9 text-center fade-up" data-testid={testid}>
          <div className="flex justify-center mb-5"><BrandMark size={64} /></div>
          {children}
        </div>
      </main>
    </div>
  );
}

function RetryButton() {
  const [err, setErr] = useState("");
  const [busy, setBusy] = useState(false);
  const go = async () => {
    setBusy(true); setErr("");
    try {
      const { data } = await api.get("/payments/latest");
      await startCheckout(data.items && data.items.length ? data.items : ["base"]);
    } catch (e) { setErr(apiError(e)); setBusy(false); }
  };
  return (
    <>
      <button type="button" onClick={go} disabled={busy} data-testid="payment-retry-btn" className="gold-btn w-full h-16 rounded-full text-xl disabled:opacity-60">
        {busy ? "Opening secure checkout…" : "Return to secure checkout"}
      </button>
      {err && <p role="alert" className="mt-3 text-[#E6CCF2]">{err}</p>}
    </>
  );
}

export function PaymentSuccess() {
  const [params] = useSearchParams();
  const sid = params.get("session_id");
  const [state, setState] = useState("checking");
  const [bought, setBought] = useState({ items: ["base"], total_pence: 999 });
  const tries = useRef(0);
  const { user, ready, refresh } = useAuth();
  const navigate = useNavigate();

  useEffect(() => {
    if (!ready) return;
    if (!user) { navigate("/signin?mode=login", { replace: true }); return; }
    if (!sid) { setState("unknown"); return; }
    let timer;
    const poll = async () => {
      try {
        const { data } = await api.get(`/payments/status/${sid}`);
        if (data.state === "paid") { setBought(data); await refresh(); setState("paid"); return; }
        if (["expired", "failed"].includes(data.state)) { setState(data.state); return; }
        tries.current += 1;
        if (tries.current >= 15) { setState(data.state === "processing" ? "processing" : "pending"); return; }
        timer = setTimeout(poll, 2000);
      } catch (e) {
        setState("unknown");
      }
    };
    poll();
    return () => clearTimeout(timer);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ready, sid]);

  if (state === "checking") return (
    <Shell testid="payment-checking">
      <Loader2 className="h-10 w-10 mx-auto text-[#B57BFF] animate-spin mb-4" aria-hidden="true" />
      <h1 className="font-serif text-3xl text-[#EDE6F7] mb-2" role="status">Confirming your payment…</h1>
      <p className="text-lg text-[#B9B3C8]">This usually takes a few seconds. Please keep this page open.</p>
    </Shell>
  );
  if (state === "paid") return (
    <Shell testid="payment-success">
      <CheckCircle2 className="h-12 w-12 mx-auto text-[#B57BFF] mb-4" aria-hidden="true" />
      <h1 className="font-serif text-3xl sm:text-4xl text-[#EDE6F7] mb-3" data-testid="payment-success-heading">Thank you — your Memories Forever purchase is complete.</h1>
      <p className="text-lg text-[#B9B3C8] mb-4">We've emailed your purchase confirmation to {user?.email}.</p>
      <ul className="text-left mx-auto max-w-sm space-y-1 text-[#D2CBE0] mb-3" data-testid="payment-success-items">
        {bought.items.includes("base") && <li>Original keepsake — up to 6 recipients, 12 recordings</li>}
        {bought.items.includes("extension") && <li>Six more people — 6 more recipients, 12 more recordings</li>}
      </ul>
      <p className="text-[#B57BFF] mb-8" data-testid="payment-success-total">{gbp(bought.total_pence)} · One-off purchase. No subscription.</p>
      {bought.items.includes("base") ? (
        <Link to="/welcome" data-testid="start-first-memory-btn" className="gold-btn inline-flex items-center justify-center w-full h-16 rounded-full text-xl">Start your first memory</Link>
      ) : (
        <Link to="/dashboard" data-testid="back-to-family-btn" className="gold-btn inline-flex items-center justify-center w-full h-16 rounded-full text-xl">Back to your family</Link>
      )}
    </Shell>
  );
  const copy = {
    pending: ["Your payment hasn't been confirmed yet", "If you completed payment, it can take a moment to confirm. You can check again, or return to checkout if you didn't finish paying."],
    processing: ["Your payment is being processed", "Your bank is still confirming this payment. We'll unlock your account and email you as soon as it's confirmed — you won't be charged twice."],
    expired: ["Your checkout timed out", "The payment wasn't completed and you haven't been charged. You can start a new secure checkout."],
    failed: ["Your payment didn't go through", "You haven't been charged. Please try again, perhaps with a different card."],
    unknown: ["We couldn't confirm this payment", "Please log in and check your account, or return to checkout."],
  }[state];
  return (
    <Shell testid={`payment-${state}`}>
      {state === "processing" || state === "pending" ? <Clock className="h-11 w-11 mx-auto text-[#B57BFF] mb-4" aria-hidden="true" /> : <XCircle className="h-11 w-11 mx-auto text-[#D9A6E8] mb-4" aria-hidden="true" />}
      <h1 className="font-serif text-3xl text-[#EDE6F7] mb-3" data-testid="payment-status-heading">{copy[0]}</h1>
      <p className="text-lg text-[#B9B3C8] mb-8">{copy[1]}</p>
      {(state === "pending" || state === "processing") && (
        <button type="button" data-testid="payment-check-again-btn" onClick={() => { tries.current = 0; setState("checking"); window.location.reload(); }}
          className="w-full h-14 rounded-full border border-[rgba(139,44,255,0.5)] text-[#B57BFF] text-lg mb-4 hover:bg-[#1E1428]">Check again</button>
      )}
      {state !== "processing" && <RetryButton />}
    </Shell>
  );
}

export function PaymentCancel() {
  const { user, ready } = useAuth();
  if (ready && !user) return (
    <Shell testid="payment-cancelled">
      <h1 className="font-serif text-3xl text-[#EDE6F7] mb-3">Payment cancelled</h1>
      <p className="text-lg text-[#B9B3C8] mb-8">You haven't been charged. Log in to continue to checkout whenever you're ready.</p>
      <Link to="/signin?mode=login" className="gold-btn inline-flex items-center justify-center w-full h-16 rounded-full text-xl">Log in</Link>
    </Shell>
  );
  return (
    <Shell testid="payment-cancelled">
      <h1 className="font-serif text-3xl text-[#EDE6F7] mb-3" data-testid="payment-cancelled-heading">Payment cancelled</h1>
      <p className="text-lg text-[#B9B3C8] mb-8">You haven't been charged, and your account is saved. You can return to secure checkout whenever you're ready.</p>
      <RetryButton />
      <Link to="/" data-testid="payment-cancel-home" className="mt-5 inline-block text-[#C9C2D8] underline underline-offset-4">Back to the home page</Link>
    </Shell>
  );
}
